import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Input,
  Popconfirm,
  Button,
  Form,
  Space,
  Row,
  Col,
  Statistic,
  Switch,
  Select,
  DatePicker,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import useTable from "../../../hooks/table";
import useUpload from "../../../hooks/upload";
import useExcel from "../../../hooks/excel";
import CustomFunctions from "../../../dependencies/custom/custom-functions";
import useModal from "../../../hooks/modal";
import Utilities from "../../../dependencies/custom/react-utilities";
import Settings from "../../../dependencies/custom/settings";
import dayjs from "dayjs";
import ValuesStore from "../../../store/values-store";

const Attendance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const details = location.state;
  const { row, readOnly } = details || {};
  const [form] = Form.useForm();
  const [showConfirm, setShowConfirm] = useState(false);
  const [date, setDate] = useState("");
  const [getDate, setGetDate] = useState("");
  const [dateType, setDateType] = useState("manualDate");
  const valuesStore = ValuesStore();

  // const excel = useExcel();
  // const upload = useUpload();
  const modal = useModal();

  const table = useTable(
    {
      pagination: {
        current: 1,
        pageSize: 20,
        hideOnSinglePage: true,
      },
    },
    undefined,
    undefined,
    undefined,
    undefined,
    "id",
    {},
    {},
  );

  const attendance_remark = useMemo(() => {
    return valuesStore.getValue("attendance_remark");
  }, []);

  const columns = [
    {
      title: "Student ID",
      dataIndex: "id",
      key: "id",
      render: (v) => <label>{v}</label>,
    },
    {
      title: "Student",
      key: "name",
      render: (v, record) => (
        <Form.Item
          name={[record.id, "studentID"]}
          noStyle
          initialValue={record.id}
        >
          <label>
            {record?.firstName} {record?.lastName} {record?.middleName}
          </label>
        </Form.Item>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Form.Item name={[record.id, "status"]} valuePropName="checked">
          <Switch checkedChildren="Present" unCheckedChildren="Absent" />
        </Form.Item>
      ),
    },
    {
      title: "Remarks",
      key: "remarks",
      width: 250,
      render: (_, record) => (
        <Form.Item
          name={[record.id, "remark"]}
          dependencies={[[record.id, "status"]]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const status = getFieldValue([record.id, "status"]);
                if ((status === false || status === undefined) && !value) {
                  return Promise.reject(
                    new Error("Remarks are required when absent"),
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Select
            mode="tags"
            maxCount={1}
            maxTagCount={1}
            style={{ width: "100%" }}
            tokenSeparators={[","]}
            options={
              attendance_remark?.map((v) => ({
                value: v?.remark,
                label: v?.remark,
              })) || []
            }
          />
        </Form.Item>
      ),
    },
  ];

  useEffect(() => {
    // console.log(row)
    table.setColumns(columns);
    CustomFunctions.getStudents(table, row, navigate);
  }, []);

  function disableFutureDates(current) {
    // current is a dayjs object
    return current && current > dayjs().endOf("day"); // disables any date strictly after today
  }
  function onDateChange(date, dateString, type = "date") {
    if (date) {
      if (type == "date") setDate(date.format("YYYY-MM-DD HH:mm"));
      else setGetDate(date.format("YYYY-MM-DD HH:mm"));
    } else {
      if (type == "date") setDate(null);
      else setGetDate(null);
    }
  }

  const onFinish = async () => {
    try {
      const values = await form.validateFields(); // throws if invalid

      const normalized = {};
      for (const studentId in values) {
        normalized[studentId] = {
          ...values[studentId],
          status: values[studentId].status ? "Present" : "Absent",
          remark: Array.isArray(values[studentId].remark)
            ? values[studentId].remark.join(", ")
            : values[studentId].remark,
        };
      }
      const data = { subjectTeacherID: row?.id, date, students: normalized };

      const res = await Utilities.fetchWithAuth(
        "POST",
        Settings?.urls?.academics?.attendance_save,
        data,
        navigate,
      );
      if (res?.ok) {
        Utilities.showNotification(
          undefined,
          "Attendance saved successfully",
          "text-success",
        );
      } else {
        Utilities.showNotification(undefined, res?.msg);
      }
      setShowConfirm(false);
    } catch (err) {
      console.log("Validation failed:", err);
    }
  };

  async function getAttendance() {
    const res = await Utilities.fetchWithAuth(
      "GET",
      `${Settings?.urls?.academics?.attendance}/${row?.id}?${dateType}=${getDate?.split(" ")[0]}`,
      null,
      navigate,
    );
    if (res?.ok) {
      if (!res?.data?.length) {
        Utilities.showNotification(undefined, "No data found");
        setGetDate("");
        return;
      }
      const data = Utilities.groupBy(res?.data || [], "studentID");
      const normalized = {};
      let recordedDate = "";
      for (const studentID in data) {
        const values = data[studentID]?.[0] || {};
        recordedDate = values?.date;
        normalized[studentID] = {
          studentID: studentID,
          status: values?.status == "Present" ? true : false,
          remark: values?.remark ? [values?.remark] : undefined,
        };
      }
      form.setFieldsValue(normalized);
      if (dateType == "manualDate") {
        setDate(recordedDate);
      }
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
    reset();
  }

  function reset() {
    modal.setOpen(false);
    setGetDate("");
  }

  function toggleDateType(v) {
    setDateType(v ? "systemDate" : "manualDate");
  }

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12} lg={8}>
        <Card variant="borderless" className="shadow">
          <Statistic
            title="Class"
            value={row?.className}
            suffix={row?.streamName}
          />
        </Card>
      </Col>

      <Col xs={24} md={12} lg={4}>
        <Card variant="borderless" className="shadow">
          <Statistic
            title="Academic Year"
            value={row?.academicYear}
            formatter={(val) => val}
          />
        </Card>
      </Col>

      <Col xs={24} md={12} lg={4}>
        <Card variant="borderless" className="shadow">
          <Statistic title="Term" value={row?.term} />
        </Card>
      </Col>

      {row?.subjectName && (
        <Col xs={24} md={12} lg={8}>
          <Card variant="borderless" className="shadow">
            <Statistic title="Subject" value={row?.subjectName} />
          </Card>
        </Col>
      )}

      <Col xs={24} md={12} lg={8}>
        <Card variant="borderless" className="shadow">
          <Statistic title="Teacher" value={row?.teacherName} />
        </Card>
      </Col>

      <Col xs={24}>
        <Card
          title={
            <>
              <Button
                className="me-1"
                onClick={() => navigate(-1)}
                icon={<ArrowLeftOutlined />}
              >
                Back
              </Button>{" "}
              Attendance Entry
            </>
          }
          bordered={false}
          extra={
            <Space wrap className="my-3">
              <DatePicker
                value={date ? dayjs(date) : null}
                onChange={(date, dateString) => onDateChange(date, dateString)}
                disabledDate={disableFutureDates}
                showTime={{ format: "HH:mm" }}
                format="YYYY-MM-DD HH:mm"
              />
              <Popconfirm
                title="Attendance Submission"
                description="Confirm attendance submission"
                onConfirm={onFinish}
                onCancel={() => setShowConfirm(false)}
                placement="left"
                okText="Yes"
                cancelText="No"
                open={showConfirm}
              >
                <Button
                  icon={<i className="fas fa-save" />}
                  type="primary"
                  // className="btn-success border-0"
                  onClick={() => setShowConfirm(true)}
                >
                  Submit
                </Button>
              </Popconfirm>
              <Button
                icon={<i className="fas fa-paper-plane" />}
                className="btn-success border-0"
                onClick={() => modal.setOpen(true)}
              >
                Fetch Attendance
              </Button>
            </Space>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={24}>
              <Form form={form} layout="vertical">
                <div className="table-responsivex">
                  {table.tableWithHeaderFooter(
                    CustomFunctions.tblCaption,
                    CustomFunctions.tblCaption,
                    {
                      scroll: { x: "max-content" },
                      tableLayout: "fixed",
                    },
                  )}
                </div>
              </Form>
            </Col>
          </Row>
        </Card>
      </Col>
      {modal.modalJSX(
        getAttendance,
        <Space direction="vertical">
          <DatePicker
            value={getDate ? dayjs(getDate) : null}
            onChange={(date, dateString) =>
              onDateChange(date, dateString, "getDate")
            }
            disabledDate={disableFutureDates}
            format="YYYY-MM-DD"
          />
          {/* <Switch checkedChildren="System Date" unCheckedChildren="Manual Date" onChange={toggleDateType} /> */}
        </Space>,
        undefined,
        { title: "Choose Date" },
      )}
    </Row>
  );
};

export default Attendance;
